import React, { useEffect, useState } from 'react';
import {
  AutofillCornerComponent,
  AutofillSingleWallComponent
} from '@inter-ikea-storageone/range-pax';
import { useCore } from 'contexts/core';
import { useSelector } from 'react-redux';
import {
  autofillDoorType,
  autofillFrameLayout,
  cornerWallLeftWidth,
  cornerWallRightWidth,
  isAutofillActive,
  setAutofillActive,
  setSingleWallWidth,
  singleWallWidth
} from 'rangeConfig/pax/features/AutofillPax/AutofillPax';
import AutoFillOptimizeSpaceFrameLayoutToggle from './AutoFillOptimizeSpaceFrameLayoutToggle';
import AutoFillOptimizeSpaceDoorTypeToggle from './AutoFillOptimizeSpaceDoorTypeToggle';
import { useAppDispatch } from 'ducks/hooks';
import Box from 'ui/Box';

import { ProductDesign, ProductType } from '@inter-ikea-storageone/range';
import AutoFillLOptimizeSpaceSingleWallOptions from './AutoFillOptimizeSpaceSingleWallOptions';
import AutoFillLOptimizeSpaceCornerWallOptions from './AutoFillOptimizeSpaceCornerWallOptions';
import { getMarketProducts, getSelectedGroupId } from 'ducks/products';
import { useTestFeature } from 'optimizely/hooks';
import { CommunicationSizeComponent, ComponentIds } from '@inter-ikea-core/core';
import LinkToAutofill from 'rangeConfig/pax/features/AutofillPax/widgets/LinkToAutofill';
import { getMajorityFrameDepth, supportsCornerFrames } from '../AutofillUtils';
import { useBehaviorSystem } from 'contexts/behaviors';
import { isMobile } from 'utils/platform';
import { getFramesInScene } from 'rangeConfig/shared/utils/colorUtils/utils';
import { getMajorityDesign } from 'rangeConfig/shared/utils/frontLayouts';
import { getSelectedRange } from 'ducks/range';
import { useServiceSettings } from 'contexts/serviceSettings';

const AutoFillOptimizeSpacePax = () => {
  const coreApi = useCore();
  const frameLayoutIndex = useSelector(autofillFrameLayout);
  const doorTypeSelection = useSelector(autofillDoorType);
  const singleWallWidthValue = useSelector(singleWallWidth);
  const cornerWallWallLeftWidth = useSelector(cornerWallLeftWidth);
  const cornerWallWallRightWidth = useSelector(cornerWallRightWidth);
  const autofillActive = useSelector(isAutofillActive);
  const autofillMobileTestEnabled = useTestFeature('PaxAutofillMobile');
  const allProducts = useSelector(getMarketProducts);
  const behaviorSystem = useBehaviorSystem();
  const selectedGroupId = useSelector(getSelectedGroupId);
  const dispatch = useAppDispatch();
  const range = useSelector(getSelectedRange);
  const framesInScene = getFramesInScene(coreApi, range);
  const [currentColor] = useState(getMajorityDesign(framesInScene)?.design || ProductDesign.WHITE);
  const { defaultFrameHeight } = useServiceSettings().dexfSettings.general;

  const onSingleWallWidthChange = (value: any) => {
    behaviorSystem.startReport(`autofill-single-width-${value}`, {
      groupId: selectedGroupId,
      menuType: 'sheet',
      widgetId: 'autofill-single-wall',
      meta: { size: { dimension: 'width', value: value } }
    });

    dispatch(setSingleWallWidth(value as number));
    dispatch(setAutofillActive(true));
  };

  const getCurrentFrameHeight = () => {
    const framesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);

    const maxHeight = framesInScene.reduce((maxHeight, frame) => {
      const height =
        frame.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
          ?.size.height || 0;
      return maxHeight < height ? height : maxHeight;
    }, 0);

    return maxHeight || Number.parseInt(defaultFrameHeight);
  };

  const getCurrentFrameDepth = () => {
    const framesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);

    const maxDepth = framesInScene.reduce((maxDepth, frame) => {
      const depth =
        frame.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
          ?.size.depth || 0;
      return maxDepth < depth ? depth : maxDepth;
    }, 0);

    return maxDepth || 580;
  };

  // When entering menu and no frames exists in scene we should activate autofill.
  useEffect(() => {
    const framesInScene = coreApi.entities.getByProductType(ProductType.PAX_FRAME);
    if (framesInScene.length === 0) {
      dispatch(setAutofillActive(true));
      autofillSingleWall();

      // If we are mobile we want to start a report for the first autofill
      if (isMobile()) {
        behaviorSystem.startReport('autofill-mobile', {
          groupId: selectedGroupId,
          menuType: 'sheet',
          widgetId: 'autofill-mobile'
        });
      }
    } else {
      behaviorSystem.endReport([`autofill-pax-link-to-sheet`]);
    }

    return () => {
      dispatch(setAutofillActive(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fill scene with corner solution frames when user changes corner options
  useEffect(() => {
    if (frameLayoutIndex === 1 && autofillActive && currentColor) {
      autofillCorner();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    cornerWallWallLeftWidth,
    cornerWallWallRightWidth,
    defaultFrameHeight,
    frameLayoutIndex,
    doorTypeSelection
  ]);

  // Fill scene with single wall frames when user changes single wall options
  useEffect(() => {
    if (frameLayoutIndex === 0 && autofillActive && currentColor) {
      autofillSingleWall();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, doorTypeSelection, frameLayoutIndex, singleWallWidthValue]);

  const autofillCorner = () => {
    const frameDepth = getMajorityFrameDepth(coreApi);
    const scene = coreApi.entities.getScene();

    const cameraDirection = coreApi.ipexViewer.get_active_camera()?.forward;

    scene?.removeComponent(AutofillSingleWallComponent.id);
    scene?.addComponent(
      new AutofillCornerComponent(
        cornerWallWallLeftWidth,
        cornerWallWallRightWidth,
        frameDepth,
        frameDepth,
        cameraDirection,
        getCurrentFrameHeight(),
        currentColor as ProductDesign,
        () => {
          behaviorSystem.endReport([
            `autofill-pax-link-to-sheet`,
            `autofill-corner-width-${cornerWallWallLeftWidth}`,
            `autofill-corner-width-${cornerWallWallRightWidth}`,
            `autofill-door-type-toggle-${doorTypeSelection}`,
            `autofill-frame-layout${frameLayoutIndex}`
          ]);
        }
      )
    );
  };

  const autofillSingleWall = () => {
    const scene = coreApi.entities.getScene();
    const cameraDirection = coreApi.ipexViewer.get_active_camera()?.forward;
    scene?.removeComponent(AutofillCornerComponent.id);
    scene?.addComponent(
      new AutofillSingleWallComponent(
        singleWallWidthValue,
        doorTypeSelection,
        cameraDirection,
        currentColor as ProductDesign,
        getCurrentFrameHeight(),
        getCurrentFrameDepth(),
        () => {
          behaviorSystem.endReport([
            `autofill-pax-link-to-sheet`,
            `autofill-single-width-${singleWallWidthValue}`,
            `autofill-door-type-toggle-${doorTypeSelection}`,
            `autofill-frame-layout${frameLayoutIndex}`,
            'autofill-mobile'
          ]);
        }
      )
    );
  };

  return (
    <>
      {autofillMobileTestEnabled && isMobile() && <LinkToAutofill />}
      {supportsCornerFrames(allProducts) && <AutoFillOptimizeSpaceFrameLayoutToggle />}
      <AutoFillOptimizeSpaceDoorTypeToggle />

      <Box my="l">
        {frameLayoutIndex === 0 ? (
          <AutoFillLOptimizeSpaceSingleWallOptions
            doorTypeSelection={doorTypeSelection}
            onChange={onSingleWallWidthChange}
            value={singleWallWidthValue}
          />
        ) : (
          <AutoFillLOptimizeSpaceCornerWallOptions />
        )}
      </Box>
    </>
  );
};

export default AutoFillOptimizeSpacePax;
